import { Node } from '@tiptap/core';
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model';
import { isHistoryTransaction } from '@tiptap/pm/history';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import {
  MAX_CAPTION_CHARS,
  MAX_IMAGES_PER_SCRIPT,
  REF_IMAGE_NODE,
} from '../../../shared/refImages';
import { getRefImageSource, refImageUrl, uploadRefImage, ImageUploadError } from '../../lib/refImageSource';
import { checkImageFile, processImageFile, ImageProcessError } from '../../lib/refImageProcess';
import { showToast } from '../Toaster';

// Reference image (finishing bundle 2B, C11). An atom that lives only at the
// end of a Visual cell, under its text (`scriptCell` content is
// `paragraph+ refImage*`). ProseMirror can't vary content by the cell's
// `kind` attribute, so "Visual cells only" is enforced by the insert/paste/
// drop plugin here, by `validateDoc` on the worker and by the drift test.
//
// Mock-up: mockups/finishing/06-reference-images-read-through.html §01, §02.

export interface RefImageOptions {
  /** Henry's editor: caption field, Replace and remove, uploads. False on
   *  every client-facing editor (review and suggest mode), where images are
   *  read-only and the enlarged view opens on tap. */
  authoring: boolean;
}

/** Fired on the figure when a client taps an image; RefImageLightbox listens. */
export const REF_IMAGE_OPEN_EVENT = 'hsp-ref-image-open';

const WRONG_CELL_MESSAGE = 'Images go in Visual cells. Paste it into the shot it belongs to.';
const LIMIT_MESSAGE =
  'This script has 30 images, the most one script can hold. Remove one to add another.';
const NOTICE_MS = 8000;

const ICONS = {
  swap: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h13l-3-3M20 17H7l3 3"/></svg>',
  expand: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>',
  warn: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></svg>',
  upload: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 16V4M7 9l5-5 5 5"/><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/></svg>',
  ban: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m5.6 5.6 12.8 12.8"/></svg>',
};

// ---------- Cell helpers ----------

/** Position of the scriptCell holding `pos`, with that cell, or null. */
function cellAt(doc: PMNode, pos: number): { pos: number; node: PMNode } | null {
  const $pos = doc.resolve(Math.max(0, Math.min(pos, doc.content.size)));
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'scriptCell') return { pos: $pos.before(depth), node };
  }
  // A node selection on an image sits between the cell's children.
  const after = $pos.nodeAfter;
  if (after?.type.name === 'scriptCell') return { pos: $pos.pos, node: after };
  return null;
}

function isVisualCell(node: PMNode | null | undefined): boolean {
  return node?.type.name === 'scriptCell' && node.attrs.kind === 'visuals';
}

/** The Visual cell the selection is in, or null (Audio, prose, nothing). */
export function selectedVisualCell(state: EditorState): number | null {
  const cell = cellAt(state.doc, state.selection.from);
  return cell && isVisualCell(cell.node) ? cell.pos : null;
}

function countImages(doc: PMNode): number {
  let n = 0;
  doc.descendants((node) => {
    if (node.type.name === REF_IMAGE_NODE) n++;
    return node.type.name !== REF_IMAGE_NODE;
  });
  return n;
}

// ---------- Upload state (decorations only: nothing pending enters the doc) ----------

interface Pending {
  id: string;
  /** Start of the Visual cell the image will land in, mapped as the doc changes. */
  cellPos: number;
  state: 'resizing' | 'uploading' | 'failed' | 'notice';
  fileName: string;
  message?: string;
  file?: File;
  heic?: boolean;
  previewUrl?: string;
}

interface UploadState {
  pending: Pending[];
  drag: { cellPos: number; ok: boolean } | null;
}

type UploadMeta =
  | { add: Pending }
  | { update: Pick<Pending, 'id'> & Partial<Pending> }
  | { remove: string }
  | { drag: UploadState['drag'] };

export const refImageUploadKey = new PluginKey<UploadState>('hsp-ref-image-uploads');

// Widget DOM, kept per pending upload so progress can update in place.
const pendingDom = new Map<string, HTMLElement>();
let nextPendingId = 1;

function pendingElement(id: string): HTMLElement {
  let el = pendingDom.get(id);
  if (!el) {
    el = document.createElement('div');
    el.className = 'ref-pending';
    el.setAttribute('contenteditable', 'false');
    pendingDom.set(id, el);
  }
  return el;
}

function renderPending(view: EditorView, p: Pending): void {
  const el = pendingElement(p.id);
  if (el.dataset.state === p.state && el.dataset.preview === (p.previewUrl ?? '')) return;
  el.dataset.state = p.state;
  el.dataset.preview = p.previewUrl ?? '';
  el.replaceChildren();
  if (p.state === 'resizing' || p.state === 'uploading') {
    const frame = document.createElement('div');
    frame.className = 'ref-frame ref-frame-busy';
    if (p.previewUrl) {
      const img = document.createElement('img');
      img.src = p.previewUrl;
      img.alt = '';
      img.onerror = () => img.remove();
      frame.appendChild(img);
    }
    const bar = document.createElement('div');
    bar.className = 'ref-upbar';
    bar.setAttribute('role', 'status');
    bar.innerHTML =
      `<div class="ref-upbar-label"><span>${p.state === 'resizing' ? 'Resizing' : 'Uploading'}</span>` +
      '<span class="ref-upbar-pct"></span></div><div class="ref-upbar-track"><i></i></div>';
    frame.appendChild(bar);
    el.appendChild(frame);
    return;
  }
  const line = document.createElement('div');
  line.className = 'ref-errline';
  line.setAttribute('role', 'alert');
  line.innerHTML = ICONS.warn;
  const text = document.createElement('span');
  if (p.state === 'failed') {
    text.append(`Couldn't upload ${p.fileName}. `);
    const retry = linkButton('Retry', () => void runUpload(view, p.id));
    const remove = linkButton('Remove', () => removePending(view, p.id));
    text.append(retry, ' · ', remove);
  } else {
    text.textContent = p.message ?? '';
  }
  line.appendChild(text);
  el.appendChild(line);
}

function linkButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ref-link';
  button.textContent = label;
  button.addEventListener('mousedown', (e) => e.preventDefault());
  button.addEventListener('click', (e) => {
    e.preventDefault();
    onClick();
  });
  return button;
}

function setProgress(id: string, fraction: number): void {
  const el = pendingDom.get(id);
  if (!el) return;
  const pct = Math.round(Math.min(1, Math.max(0, fraction)) * 100);
  const bar = el.querySelector<HTMLElement>('.ref-upbar-track i');
  const label = el.querySelector<HTMLElement>('.ref-upbar-pct');
  if (bar) bar.style.width = `${pct}%`;
  if (label) label.textContent = `${pct}%`;
}

function findPending(state: EditorState, id: string): Pending | undefined {
  return refImageUploadKey.getState(state)?.pending.find((p) => p.id === id);
}

function dispatchMeta(view: EditorView, meta: UploadMeta, tr: Transaction = view.state.tr): void {
  if (view.isDestroyed) return;
  view.dispatch(tr.setMeta(refImageUploadKey, meta));
}

function removePending(view: EditorView, id: string): void {
  const p = findPending(view.state, id);
  if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
  pendingDom.delete(id);
  dispatchMeta(view, { remove: id });
}

function addNotice(view: EditorView, cellPos: number, message: string): void {
  const id = `n${nextPendingId++}`;
  dispatchMeta(view, { add: { id, cellPos, state: 'notice', fileName: '', message } });
  window.setTimeout(() => {
    if (!view.isDestroyed && findPending(view.state, id)) removePending(view, id);
  }, NOTICE_MS);
}

/** Resize, upload and place one pending image. Also Retry's entry point. */
async function runUpload(view: EditorView, id: string): Promise<void> {
  const start = findPending(view.state, id);
  const source = getRefImageSource();
  if (!start?.file || source?.kind !== 'admin') return;
  const file = start.file;
  const preview = start.previewUrl ?? URL.createObjectURL(file);
  dispatchMeta(view, { update: { id, state: 'resizing', previewUrl: preview } });
  try {
    const processed = await processImageFile(file, start.heic === true);
    const processedUrl = URL.createObjectURL(processed.blob);
    if (!findPending(view.state, id)) return URL.revokeObjectURL(processedUrl);
    URL.revokeObjectURL(preview);
    dispatchMeta(view, { update: { id, state: 'uploading', previewUrl: processedUrl } });
    setProgress(id, 0);
    const uploaded = await uploadRefImage(source.scriptId, processed, (f) => setProgress(id, f));
    const current = findPending(view.state, id);
    if (!current || view.isDestroyed) return; // the cell (or the editor) went away
    const cell = view.state.doc.nodeAt(current.cellPos);
    URL.revokeObjectURL(processedUrl);
    pendingDom.delete(id);
    if (!isVisualCell(cell)) return dispatchMeta(view, { remove: id });
    const node = view.state.schema.nodes[REF_IMAGE_NODE]!.create({
      imageId: uploaded.id,
      caption: '',
      width: uploaded.width,
      height: uploaded.height,
    });
    // Always after the cell's text and any images already there.
    const tr = view.state.tr.insert(current.cellPos + cell!.nodeSize - 1, node);
    dispatchMeta(view, { remove: id }, tr);
  } catch (err) {
    if (!findPending(view.state, id)) return;
    const message =
      err instanceof ImageProcessError
        ? err.message
        : err instanceof ImageUploadError && [409, 413, 415].includes(err.status)
        ? err.message
        : null;
    if (message) {
      const p = findPending(view.state, id);
      if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
      pendingDom.delete(id);
      dispatchMeta(view, { remove: id });
      addNotice(view, start.cellPos, message);
    } else {
      dispatchMeta(view, { update: { id, state: 'failed', previewUrl: undefined } });
    }
  }
}

function busyCount(state: EditorState): number {
  return (refImageUploadKey.getState(state)?.pending ?? []).filter(
    (p) => p.state === 'resizing' || p.state === 'uploading' || p.state === 'failed',
  ).length;
}

/**
 * Add image files to a Visual cell: the dock's Image button, paste and drop
 * all land here. `cellPos` defaults to the cell holding the selection.
 */
export function insertRefImageFiles(view: EditorView, files: File[], cellPos?: number | null): void {
  if (!view.editable || getRefImageSource()?.kind !== 'admin') return;
  const target = cellPos ?? selectedVisualCell(view.state);
  if (target === null || !isVisualCell(view.state.doc.nodeAt(target))) {
    showToast('error', WRONG_CELL_MESSAGE);
    return;
  }
  for (const file of files) {
    const check = checkImageFile(file);
    if (!check.ok) {
      addNotice(view, target, check.message);
      continue;
    }
    if (countImages(view.state.doc) + busyCount(view.state) >= MAX_IMAGES_PER_SCRIPT) {
      addNotice(view, target, LIMIT_MESSAGE);
      return;
    }
    const id = `u${nextPendingId++}`;
    dispatchMeta(view, {
      add: { id, cellPos: target, state: 'resizing', fileName: file.name || 'image', file, heic: check.heic },
    });
    void runUpload(view, id);
  }
}

/** Swap an image's file in place, keeping its caption (the Replace tool). */
export async function replaceImage(view: EditorView, getPos: () => number | undefined, file: File): Promise<void> {
  const source = getRefImageSource();
  if (source?.kind !== 'admin') return;
  const pos = getPos();
  if (typeof pos !== 'number') return;
  const imageId = view.state.doc.nodeAt(pos)?.attrs.imageId;
  if (!imageId) return;
  const cell = cellAt(view.state.doc, pos);
  const check = checkImageFile(file);
  if (!check.ok) {
    if (cell) addNotice(view, cell.pos, check.message);
    return;
  }
  try {
    const processed = await processImageFile(file, check.heic);
    const uploaded = await uploadRefImage(source.scriptId, processed, () => {}, imageId);
    const at = getPos();
    const node = typeof at === 'number' ? view.state.doc.nodeAt(at) : null;
    if (!node || node.type.name !== REF_IMAGE_NODE || node.attrs.imageId !== imageId || view.isDestroyed) return;
    view.dispatch(
      view.state.tr.setNodeMarkup(at!, undefined, {
        ...node.attrs,
        imageId: uploaded.id,
        width: uploaded.width,
        height: uploaded.height,
      }),
    );
  } catch (err) {
    const message =
      err instanceof ImageProcessError || err instanceof ImageUploadError
        ? err.message === 'Network error' || err.message === 'Upload failed'
          ? `Couldn't upload ${file.name || 'that image'}.`
          : err.message
        : `Couldn't upload ${file.name || 'that image'}.`;
    if (cell) addNotice(view, cell.pos, message);
  }
}

function imageFiles(list: FileList | null | undefined): File[] {
  return Array.from(list ?? []);
}

function hasFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

function dropTarget(view: EditorView, event: DragEvent): { cellPos: number; ok: boolean } | null {
  const found = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (!found) return null;
  const cell = cellAt(view.state.doc, found.inside >= 0 ? found.inside : found.pos);
  return cell ? { cellPos: cell.pos, ok: isVisualCell(cell.node) } : null;
}

function overlay(ok: boolean): HTMLElement {
  const el = document.createElement('div');
  el.className = `ref-dropmsg ${ok ? 'ref-dropmsg-ok' : 'ref-dropmsg-no'}`;
  el.setAttribute('contenteditable', 'false');
  el.innerHTML = ok ? ICONS.upload : ICONS.ban;
  el.append(ok ? 'Drop to add a reference image' : 'Images go in the Visual column');
  return el;
}

function uploadPlugin(): Plugin<UploadState> {
  return new Plugin<UploadState>({
    key: refImageUploadKey,
    state: {
      init: () => ({ pending: [], drag: null }),
      apply(tr, value) {
        let pending = value.pending;
        let drag = value.drag;
        if (tr.docChanged) {
          pending = pending.flatMap((p) => {
            const mapped = tr.mapping.mapResult(p.cellPos, 1);
            return mapped.deleted ? [] : [{ ...p, cellPos: mapped.pos }];
          });
          drag = null;
        }
        const meta = tr.getMeta(refImageUploadKey) as UploadMeta | undefined;
        if (meta && 'add' in meta) pending = [...pending, meta.add];
        if (meta && 'update' in meta) {
          pending = pending.map((p) => (p.id === meta.update.id ? { ...p, ...meta.update } : p));
        }
        if (meta && 'remove' in meta) pending = pending.filter((p) => p.id !== meta.remove);
        if (meta && 'drag' in meta) drag = meta.drag;
        return pending === value.pending && drag === value.drag ? value : { pending, drag };
      },
    },
    view: (view) => ({
      update: (v) => {
        for (const p of refImageUploadKey.getState(v.state)?.pending ?? []) renderPending(v, p);
      },
      destroy: () => {
        for (const p of refImageUploadKey.getState(view.state)?.pending ?? []) {
          if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
          pendingDom.delete(p.id);
        }
      },
    }),
    props: {
      decorations(state) {
        const value = refImageUploadKey.getState(state);
        if (!value || (value.pending.length === 0 && !value.drag)) return null;
        const decos: Decoration[] = [];
        for (const p of value.pending) {
          const cell = state.doc.nodeAt(p.cellPos);
          if (!cell || cell.type.name !== 'scriptCell') continue;
          decos.push(
            Decoration.widget(p.cellPos + cell.nodeSize - 1, () => pendingElement(p.id), {
              key: `ref-pending-${p.id}`,
              side: 1,
              ignoreSelection: true,
              stopEvent: () => true,
            }),
          );
        }
        if (value.drag) {
          const cell = state.doc.nodeAt(value.drag.cellPos);
          if (cell && cell.type.name === 'scriptCell') {
            const ok = value.drag.ok;
            decos.push(
              Decoration.node(value.drag.cellPos, value.drag.cellPos + cell.nodeSize, {
                class: ok ? 'ref-drop-ok' : 'ref-drop-no',
              }),
              Decoration.widget(value.drag.cellPos + 1, () => overlay(ok), {
                key: `ref-drop-${ok}`,
                side: -1,
                ignoreSelection: true,
              }),
            );
          }
        }
        return DecorationSet.create(state.doc, decos);
      },
      handlePaste(view, event) {
        const files = imageFiles(event.clipboardData?.files).filter((f) => f.type.startsWith('image/') || checkImageFile(f).ok);
        if (files.length === 0 || prefersTextPaste(event)) return false;
        const cellPos = selectedVisualCell(view.state);
        if (cellPos === null) {
          showToast('error', WRONG_CELL_MESSAGE);
          return true;
        }
        insertRefImageFiles(view, files, cellPos);
        return true;
      },
      handleDrop(view, event, _slice, moved) {
        const files = imageFiles(event.dataTransfer?.files);
        if (moved || files.length === 0) return false;
        const target = dropTarget(view, event);
        dispatchMeta(view, { drag: null });
        // Releasing over an Audio cell (or prose) does nothing at all.
        if (target?.ok) insertRefImageFiles(view, files, target.cellPos);
        return true;
      },
      handleDOMEvents: {
        dragover(view, event) {
          if (!hasFiles(event)) return false;
          const target = dropTarget(view, event);
          const current = refImageUploadKey.getState(view.state)?.drag ?? null;
          if (target?.ok) event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = target?.ok ? 'copy' : 'none';
          if (target?.cellPos !== current?.cellPos || target?.ok !== current?.ok) {
            dispatchMeta(view, { drag: target });
          }
          return false;
        },
        dragleave(view, event) {
          const related = event.relatedTarget as globalThis.Node | null;
          if (related && view.dom.contains(related)) return false;
          if (refImageUploadKey.getState(view.state)?.drag) dispatchMeta(view, { drag: null });
          return false;
        },
        dragend(view) {
          if (refImageUploadKey.getState(view.state)?.drag) dispatchMeta(view, { drag: null });
          return false;
        },
      },
    },
  });
}

// ---------- Client editors: images are read-only in suggest mode ----------

function imageSignature(doc: PMNode): { images: string[]; rows: number } {
  const images: string[] = [];
  let rows = 0;
  doc.descendants((node, _pos, parent) => {
    if (node.type.name === 'scriptRow') rows++;
    if (node.type.name === REF_IMAGE_NODE && parent?.attrs.kind === 'visuals') {
      images.push(JSON.stringify(node.attrs));
      return false;
    }
    return true;
  });
  return { images, rows };
}

/** Prefer an Office/web text selection over its accompanying PNG preview. */
function prefersTextPaste(event: ClipboardEvent): boolean {
  const html = event.clipboardData?.getData('text/html') ?? '';
  const text = event.clipboardData?.getData('text/plain') ?? '';
  if (html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    // Wrapping tags around a single image do not make it a text selection.
    if (!parsed.body.textContent?.trim() && parsed.body.querySelectorAll('img').length === 1
      && !parsed.body.querySelector('table,li,hr,video')) return false;
  }
  return !!(html || text);
}

/**
 * Clients see images while suggesting edits but can't add, recaption or
 * remove them on their own. Deleting a whole row still takes its picture
 * with it. The server restores master images regardless (refImages.ts).
 */
function readOnlyImagesPlugin(): Plugin {
  let known = new Set<string>();
  return new Plugin({
    key: new PluginKey('hsp-ref-image-readonly'),
    state: {
      init: (_config, state) => { known = new Set(imageSignature(state.doc).images); return null; },
      apply: () => null,
    },
    filterTransaction(tr, state) {
      if (!tr.docChanged || isHistoryTransaction(tr)) return true;
      const before = imageSignature(state.doc);
      const after = imageSignature(tr.doc);
      if (after.images.some((sig) => !known.has(sig))) return false;
      if (new Set(after.images).size !== after.images.length) return false;
      if (after.images.length < before.images.length && after.rows >= before.rows) return false;
      return true;
    },
    props: {
      handlePaste(_view, event) {
        return imageFiles(event.clipboardData?.files).length > 0 && !prefersTextPaste(event);
      },
      handleDrop(_view, event) {
        return imageFiles(event.dataTransfer?.files).length > 0;
      },
    },
  });
}

/** Schema attributes cannot constrain cell content. Repair all transaction
 * sources, including copied ProseMirror slices, in both extension sets. */
function placementPlugin(): Plugin {
  // Clipboard transforms also run for drops. Use the pointer's destination,
  // rather than the selection left behind at the drag source.
  let dropPos: number | null | undefined;
  const cleanSlice = (slice: Slice, view: EditorView, pos: number | null) => {
    if (pos !== null && isVisualCell(cellAt(view.state.doc, pos)?.node)) return slice;
    let removed = false;
    const strip = (fragment: Fragment): Fragment => {
      const nodes: PMNode[] = [];
      fragment.forEach((node) => {
        if (node.type.name === REF_IMAGE_NODE) {
          removed = true;
        } else if (node.isLeaf) {
          nodes.push(node);
        } else {
          const content = strip(node.content);
          if (content.size || node.content.size === 0) nodes.push(node.copy(content));
        }
      });
      return Fragment.fromArray(nodes);
    };
    const content = strip(slice.content);
    if (!removed) return slice;
    showToast('error', WRONG_CELL_MESSAGE);
    if (!content.size) return Slice.empty;
    const max = Slice.maxOpen(content);
    return new Slice(content, Math.min(slice.openStart, max.openStart), Math.min(slice.openEnd, max.openEnd));
  };
  return new Plugin({
    key: new PluginKey('hsp-ref-image-placement'),
    props: {
      handleDOMEvents: {
        drop(view, event) {
          dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null;
          queueMicrotask(() => { dropPos = undefined; });
          return false;
        },
      },
      transformPasted(slice, view) {
        return cleanSlice(slice, view, dropPos === undefined ? view.state.selection.from : dropPos);
      },
      // An empty replacement would otherwise delete the current selection (or
      // the drag source). File transfers belong to the upload/read-only plugin.
      handlePaste: (_view, event, slice) => !slice.content.size && !event.clipboardData?.files.length,
      handleDrop: (_view, event, slice) => !slice.content.size && !event.dataTransfer?.files.length,
    },
    appendTransaction(transactions, _oldState, state) {
      if (!transactions.some((tr) => tr.docChanged)) return null;
      const remove: number[] = [];
      let count = 0;
      state.doc.descendants((node, pos, parent) => {
        if (node.type.name !== REF_IMAGE_NODE) return true;
        if (parent?.type.name !== 'scriptCell' || parent.attrs.kind !== 'visuals' || ++count > MAX_IMAGES_PER_SCRIPT) remove.push(pos);
        return false;
      });
      if (!remove.length) return null;
      const tr = state.tr;
      for (const pos of remove.reverse()) tr.delete(pos, pos + 1);
      return tr;
    },
  });
}

// ---------- Node view ----------

function cellTextFor(view: EditorView, pos: number): string {
  const cell = cellAt(view.state.doc, pos);
  return cell ? cell.node.textContent.trim().replace(/\s+/g, ' ').slice(0, 160) : '';
}

function altFor(caption: string, cellText: string): string {
  if (caption.trim()) return caption.trim();
  return cellText ? `Reference image for: ${cellText}` : 'Reference image';
}

export const RefImage = Node.create<RefImageOptions>({
  name: REF_IMAGE_NODE,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { authoring: false };
  },

  addAttributes() {
    return {
      imageId: { default: null },
      caption: { default: '' },
      width: { default: null },
      height: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure[data-type="ref-image"]',
        getAttrs: (el) => {
          const element = el as HTMLElement;
          const imageId = element.getAttribute('data-image-id');
          if (!imageId) return false;
          return {
            imageId,
            caption: element.getAttribute('data-caption') ?? '',
            width: Number(element.getAttribute('data-width')) || null,
            height: Number(element.getAttribute('data-height')) || null,
          };
        },
      },
    ];
  },

  // Used where there is no node view: the compare-mode diff and copy/paste.
  renderHTML({ node }) {
    const { imageId, caption, width, height } = node.attrs as {
      imageId: string | null;
      caption: string;
      width: number | null;
      height: number | null;
    };
    const src = imageId ? refImageUrl(imageId) : null;
    return [
      'figure',
      {
        'data-type': 'ref-image',
        'data-image-id': imageId ?? '',
        'data-caption': caption ?? '',
        'data-width': width ?? '',
        'data-height': height ?? '',
        class: 'ref-figure',
      },
      [
        'div',
        { class: 'ref-frame', style: width && height ? `aspect-ratio: ${width} / ${height}` : '' },
        ['img', { src: src ?? '', alt: caption || 'Reference image', loading: 'lazy' }],
      ],
      ...(caption ? [['figcaption', { class: 'ref-caption' }, caption]] : []),
    ] as never;
  },

  addProseMirrorPlugins() {
    return [placementPlugin(), ...(this.options.authoring ? [uploadPlugin()] : [readOnlyImagesPlugin()])];
  },

  addNodeView() {
    const authoringOption = this.options.authoring;
    return ({ node, getPos, editor }) => {
      let current: PMNode = node;
      const authoring = authoringOption && editor.isEditable;
      // The enlarged view belongs to the client's read-only page only.
      const lightbox = !authoringOption && !editor.isEditable;
      const pos = () => (typeof getPos === 'function' ? getPos() : undefined);

      const dom = document.createElement('figure');
      dom.className = 'ref-figure';
      dom.setAttribute('data-type', 'ref-image');
      dom.setAttribute('contenteditable', 'false');

      const frame = document.createElement('div');
      frame.className = 'ref-frame';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.decoding = 'async';
      img.draggable = false;
      const missing = document.createElement('div');
      missing.className = 'ref-missing';
      missing.textContent = 'Image unavailable';
      missing.hidden = true;
      img.addEventListener('error', () => {
        img.hidden = true;
        missing.hidden = false;
      });
      img.addEventListener('load', () => {
        img.hidden = false;
        missing.hidden = true;
      });
      frame.append(img, missing);
      dom.appendChild(frame);

      let captionInput: HTMLInputElement | null = null;
      let captionText: HTMLElement | null = null;

      if (lightbox) {
        frame.setAttribute('role', 'button');
        frame.tabIndex = 0;
        const mark = document.createElement('span');
        mark.className = 'ref-expand';
        mark.setAttribute('aria-hidden', 'true');
        mark.innerHTML = ICONS.expand;
        frame.appendChild(mark);
        const open = () =>
          dom.dispatchEvent(new CustomEvent(REF_IMAGE_OPEN_EVENT, { bubbles: true, detail: { figure: dom } }));
        frame.addEventListener('click', open);
        frame.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        });
      }

      if (authoring) {
        const tools = document.createElement('div');
        tools.className = 'ref-tools';
        const picker = document.createElement('input');
        picker.type = 'file';
        picker.accept = 'image/*,.heic,.heif';
        picker.hidden = true;
        picker.addEventListener('change', () => {
          const file = picker.files?.[0];
          picker.value = '';
          if (file) void replaceImage(editor.view, pos, file);
        });
        const replace = document.createElement('button');
        replace.type = 'button';
        replace.className = 'ref-tool';
        replace.innerHTML = `${ICONS.swap}<span>Replace</span>`;
        replace.addEventListener('mousedown', (e) => e.preventDefault());
        replace.addEventListener('click', () => picker.click());
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ref-tool ref-tool-x';
        remove.setAttribute('aria-label', 'Remove image');
        remove.title = 'Remove image';
        remove.textContent = '×';
        remove.addEventListener('mousedown', (e) => e.preventDefault());
        remove.addEventListener('click', () => {
          const at = pos();
          if (typeof at !== 'number') return;
          // The stored file stays: version history may still show it.
          editor.view.dispatch(editor.view.state.tr.delete(at, at + current.nodeSize));
          editor.view.focus();
        });
        tools.append(replace, remove, picker);
        frame.appendChild(tools);

        captionInput = document.createElement('input');
        captionInput.type = 'text';
        captionInput.className = 'ref-caption-input';
        captionInput.maxLength = MAX_CAPTION_CHARS;
        captionInput.placeholder = 'Add a caption (optional)';
        captionInput.setAttribute('aria-label', 'Image caption');
        const note = document.createElement('div');
        note.className = 'ref-caption-note';
        note.textContent = 'Caption · shown to the client and read by screen readers';
        captionInput.addEventListener('input', () => {
          const at = pos();
          if (typeof at !== 'number' || !captionInput) return;
          // eslint-disable-next-line no-control-regex
          const caption = captionInput.value.replace(/[\u0000-\u001f]/g, ' ').slice(0, MAX_CAPTION_CHARS);
          editor.view.dispatch(
            editor.view.state.tr.setNodeMarkup(at, undefined, { ...current.attrs, caption }),
          );
        });
        captionInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
            captionInput?.blur();
            editor.view.focus();
          }
        });
        dom.append(captionInput, note);
      } else {
        captionText = document.createElement('figcaption');
        captionText.className = 'ref-caption';
        dom.appendChild(captionText);
      }

      const sync = (n: PMNode) => {
        const { imageId, caption, width, height } = n.attrs as {
          imageId: string | null;
          caption: string;
          width: number | null;
          height: number | null;
        };
        const src = imageId ? refImageUrl(imageId) : null;
        if (src && img.getAttribute('src') !== src) {
          img.hidden = false;
          missing.hidden = true;
          img.src = src;
        } else if (!src) {
          img.hidden = true;
          missing.hidden = false;
        }
        if (width && height) frame.style.aspectRatio = `${width} / ${height}`;
        const at = pos();
        img.alt = altFor(caption ?? '', typeof at === 'number' ? cellTextFor(editor.view, at) : '');
        dom.setAttribute('data-image-id', imageId ?? '');
        dom.setAttribute('data-caption', caption ?? '');
        if (lightbox) frame.setAttribute('aria-label', `Enlarge image: ${img.alt}`);
        if (captionInput && document.activeElement !== captionInput) captionInput.value = caption ?? '';
        if (captionText) {
          captionText.textContent = caption ?? '';
          captionText.hidden = !caption;
        }
      };
      sync(current);

      return {
        dom,
        update(updated: PMNode) {
          if (updated.type.name !== REF_IMAGE_NODE) return false;
          current = updated;
          sync(updated);
          return true;
        },
        selectNode() {
          dom.classList.add('is-selected');
        },
        deselectNode() {
          dom.classList.remove('is-selected');
        },
        stopEvent(event: Event) {
          const target = event.target as HTMLElement | null;
          if (!target) return false;
          if (captionInput && (target === captionInput || captionInput.contains(target))) return true;
          if (target.closest('.ref-tools')) return true;
          return lightbox && event.type !== 'mousedown' && frame.contains(target);
        },
        ignoreMutation() {
          return true;
        },
      };
    };
  },
});
